import React from 'react';
import {HashRouter as Router,Routes,Route,Navigate} from 'react-router-dom';
import {AuthProvider,useAuth} from './contexts/AuthContext';
import {ClinicProvider} from './contexts/ClinicContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Patients from './pages/Patients';
import Appointments from './pages/Appointments';
import Invoices from './pages/Invoices';
import Expenses from './pages/Expenses';
import Accounts from './pages/Accounts';
import Doctors from './pages/Doctors';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import About from './pages/About';
import Contact from './pages/Contact';
import Gallery from './pages/Gallery';
import './App.css';

const ProtectedRoute=({children,roles=[]})=> {
  const {user,loading}=useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  // Redirect unauthenticated users to login instead of dashboard
  if (!user) {
    return <Navigate to="/login" />;
  }

  // If roles are specified but don't match user role,show access denied (optional)
  if (roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

const PublicRoute=({children})=> {
  const {user}=useAuth();
  return user ? <Navigate to="/dashboard" /> : children;
};

// Unauthorized access component
const UnauthorizedAccess=()=> {
  const {user}=useAuth();

  return (
    <div className="min-h-screen flex items-center justify-center bg-grey-50">
      <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
        <div className="mb-6">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        </div>
        <h3 className="text-lg font-medium text-grey-900 mb-2">Access Denied</h3>
        <p className="text-sm text-grey-600 mb-6">
          You don't have permission to access this page. Your current role ({user?.role}) doesn't allow access to this resource.
        </p>
        <button
          onClick={()=> window.history.back()}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-maroon-600 hover:bg-maroon-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-maroon-500"
        >
          Go Back
        </button>
      </div>
    </div>
  );
};

function App() {
  return (
    <ClinicProvider>
      <Router>
        <AuthProvider>
          <div className="min-h-screen bg-grey-50">
            <Routes>
              <Route path="/login" element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } />
              {/* Homepage is now About Us */}
              <Route path="/" element={<About />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/unauthorized" element={<UnauthorizedAccess />} />
              
              <Route path="/*" element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      {/* Dashboard - All authenticated users */}
                      <Route path="/dashboard" element={<Dashboard />} />
                      
                      {/* Patients - Admin,Manager,Receptionist only */}
                      <Route path="/patients" element={
                        <ProtectedRoute roles={['admin','manager','receptionist']}>
                          <Patients />
                        </ProtectedRoute>
                      } />
                      
                      {/* Appointments - All authenticated users */}
                      <Route path="/appointments" element={<Appointments />} />
                      
                      {/* Invoices - Admin,Manager,Receptionist only */}
                      <Route path="/invoices" element={
                        <ProtectedRoute roles={['admin','manager','receptionist']}>
                          <Invoices />
                        </ProtectedRoute>
                      } />
                      
                      {/* Expenses - Admin,Manager only */}
                      <Route path="/expenses" element={
                        <ProtectedRoute roles={['admin','manager']}>
                          <Expenses />
                        </ProtectedRoute>
                      } />

                      {/* Accounts - Admin,Manager only */}
                      <Route path="/accounts" element={
                        <ProtectedRoute roles={['admin','manager']}>
                          <Accounts />
                        </ProtectedRoute>
                      } />
                      
                      {/* Doctors - Admin,Doctor only */}
                      <Route path="/doctors" element={
                        <ProtectedRoute roles={['admin','doctor']}>
                          <Doctors />
                        </ProtectedRoute>
                      } />
                      
                      {/* Reports - Admin only */}
                      <Route path="/reports" element={
                        <ProtectedRoute roles={['admin','manager']}>
                          <Reports />
                        </ProtectedRoute>
                      } />
                      
                      {/* Settings - Admin only */}
                      <Route path="/settings/*" element={
                        <ProtectedRoute roles={['admin']}>
                          <Settings />
                        </ProtectedRoute>
                      } />
                      
                      {/* Catch all unauthorized routes */}
                      <Route path="*" element={<Navigate to="/unauthorized" />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              } />
            </Routes>
          </div>
        </AuthProvider>
      </Router>
    </ClinicProvider>
  );
}

export default App;